/**
 * Property leads — the "List your property" storefront intake.
 *
 * A lead is pure intake data. It NEVER directly creates an Owner/Property/Tenant;
 * a staff "convert" action turns a reviewed lead into live PMS records.
 */

export enum LeadStatus {
  /** Storefront submit lands here; promoted to NEW once the email OTP is verified. */
  PENDING_VERIFICATION = 'pending_verification',
  NEW = 'new',
  CONTACTED = 'contacted',
  ONBOARDED = 'onboarded',
  CLOSED = 'closed',
}

export enum LeadPropertyType {
  HOTEL = 'hotel',
  RESORT = 'resort',
  APARTMENT = 'apartment',
  LODGE = 'lodge',
  VILLA = 'villa',
}

export interface PropertyLead {
  id: string;
  ownerName: string;
  email: string;
  phone: string;
  propertyName: string;
  city: string;
  country: string | null;
  propertyType: LeadPropertyType;
  roomsEstimate: number;
  message: string | null;
  status: LeadStatus;
  source: string;
  convertedOwnerId: string | null;
  convertedPropertyId: string | null;
  /** Reviewer note — request-changes feedback or a rejection reason; null until set. */
  reviewFeedback: string | null;
  /** Set when the submitter verifies their email OTP; null while pending. */
  verifiedAt: string | null;
  /** When a pending lead's OTP window lapses → eligible for purge; null once verified. */
  pendingExpiresAt: string | null;
  createdAt: string;
  updatedAt: string;
}

/** Public request body for POST /v1/public/leads. */
export interface SubmitLeadRequest {
  ownerName: string;
  email: string;
  phone: string;
  propertyName: string;
  city: string;
  country?: string;
  propertyType: LeadPropertyType;
  roomsEstimate: number;
  message?: string;
  /** Honeypot — must stay empty; bots that fill it are silently dropped. */
  companyWebsite?: string;
  /** Captcha seam — accepted but not yet verified (no-op today). */
  captchaToken?: string;
}

/** Public 201 response — intentionally minimal (guest-safe). */
export interface SubmitLeadResponse {
  ok: true;
  id: string;
  /** 6-digit OTP echoed back ONLY off-prod (NODE_ENV !== 'production') for QA/local without SMTP. */
  devCode?: string;
}

/** Public request body for POST /v1/public/leads/verify. */
export interface VerifyLeadRequest {
  email: string;
  code: string;
}

/** Public 200 response — promotes the newest pending lead for the email. */
export interface VerifyLeadResponse {
  ok: true;
  id: string;
}

/** Public request body for POST /v1/public/leads/resend. */
export interface ResendLeadOtpRequest {
  email: string;
}

/** Public 200 response — re-issues (rotates) the OTP for a live pending lead. */
export interface ResendLeadOtpResponse {
  ok: true;
  /** 6-digit OTP echoed back ONLY off-prod for QA/local without SMTP. */
  devCode?: string;
}

// ── Admin decision loop (TASK-51) ────────────────────────────────────────────

/** Body for POST /v1/admin/platform/leads/:id/request-changes. */
export interface RequestLeadChangesRequest {
  /** Required, non-empty. Persisted to review_feedback; surfaced to the submitter. */
  feedback: string;
}

/** Body for POST /v1/admin/platform/leads/:id/reject. */
export interface RejectLeadRequest {
  /** Required, non-empty. Persisted to review_feedback as the rejection reason. */
  reason: string;
}

/**
 * Response for POST /v1/admin/platform/leads/:id/approve — the (now onboarded)
 * lead plus the ids of the Owner + draft Property the convert created (or, on an
 * idempotent re-approve, the already-linked ids).
 */
export interface ApproveLeadResponse<TLead = unknown> {
  lead: TLead;
  ownerId: string;
  propertyId: string;
}
